'use client';

import type { EntityResult } from '../runtime';

import { useRunnerRequestUiRuntime } from '../runtime';

import { useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { type SlashCommandItem } from '../lib/tiptap/extensions/SlashCommand';
import { cn } from '../lib/cn';
import { BaseModal } from './BaseModal';
import { AgentTeamsLottieLoader } from './loading/AgentTeamsLottieLoader';
import { Spinner } from './loading/Spinner';

type EntityReferencePickerModalProps = {
  isOpen: boolean;
  command: SlashCommandItem;
  token: string | null;
  projectId: string;
  onCloseAction: () => void;
  onSelectAction: (entity: EntityResult) => void;
};

const SEARCH_DEBOUNCE_MS = 300;
const RESULTS_MIN_HEIGHT_CLASS_NAME = 'min-h-[22rem]';

export const EntityReferencePickerModal = ({
  isOpen,
  command,
  token,
  projectId,
  onCloseAction,
  onSelectAction,
}: EntityReferencePickerModalProps) => {
  const { searchCommandEntities } = useRunnerRequestUiRuntime();
  const t = useTranslations('components.appLayout');
  const tCommon = useTranslations('common');
  const translateBadge = useMemo(() => (key: string) => t(key as never), [t]);

  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<EntityResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const latestRequestRef = useRef(0);
  const isInitialQueryRef = useRef(true);

  // Reset transient state whenever the picker (re)opens for a command.
  useEffect(() => {
    if (!isOpen) return;
    setSearchQuery('');
    setResults([]);
    setSelectedIndex(0);
    isInitialQueryRef.current = true;
  }, [isOpen, command.id]);

  // Autofocus the search field on open.
  useEffect(() => {
    if (!isOpen) return;
    const timer = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [isOpen, command.id]);

  // Debounced + abortable search. First query for a command fires immediately so
  // the initial list appears without the debounce delay.
  useEffect(() => {
    if (!isOpen || !token || !projectId) return;

    setIsLoading(true);
    const delay = isInitialQueryRef.current ? 0 : SEARCH_DEBOUNCE_MS;
    isInitialQueryRef.current = false;

    let controller: AbortController | null = null;
    const timer = window.setTimeout(() => {
      const requestController = new AbortController();
      controller = requestController;
      const requestId = latestRequestRef.current + 1;
      latestRequestRef.current = requestId;

      void searchCommandEntities(token, projectId, command.id, searchQuery, translateBadge, {
        signal: requestController.signal,
      })
        .then((data) => {
          if (latestRequestRef.current !== requestId) return;
          setResults(data);
          setSelectedIndex(0);
          setIsLoading(false);
        })
        .catch(() => {
          if (latestRequestRef.current !== requestId || requestController.signal.aborted) return;
          setResults([]);
          setIsLoading(false);
        });
    }, delay);

    return () => {
      controller?.abort();
      window.clearTimeout(timer);
    };
  }, [isOpen, command.id, projectId, searchQuery, token, translateBadge]);

  useEffect(() => {
    optionRefs.current[selectedIndex]?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex, results]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const maxIndex = results.length - 1;

    if (event.key === 'ArrowDown') {
      if (maxIndex < 0) return;
      event.preventDefault();
      setSelectedIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      if (maxIndex < 0) return;
      event.preventDefault();
      setSelectedIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const entity = results[selectedIndex];
      if (entity) onSelectAction(entity);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onCloseAction();
    }
  };

  const handleSearchQueryChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setResults([]);
    setSelectedIndex(0);
    if (isOpen && token && projectId) setIsLoading(true);
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onCloseAction} size="lg">
      <div className="flex max-h-[70vh] flex-col">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 border-b border-base-300 px-5 py-3.5">
          <span className="flex min-w-0 items-center gap-2">
            <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center [&>span]:h-5 [&>span]:w-5 [&_svg]:h-3.5 [&_svg]:w-3.5">
              {command.icon as ReactNode}
            </span>
            <h3 className="truncate text-base font-semibold text-base-content">{command.title}</h3>
          </span>
          <button
            type="button"
            className="btn btn-ghost btn-xs btn-circle"
            onClick={onCloseAction}
            aria-label={tCommon('actions.close')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3">
          <div className="relative">
            {isLoading ? (
              <Spinner
                size="sm"
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-primary"
              />
            ) : null}
            <input
              ref={searchInputRef}
              type="text"
              className={cn('input input-bordered input-sm w-full text-sm', isLoading ? 'pl-8' : 'pl-3')}
              placeholder={t('entityPickerSearchPlaceholder')}
              aria-label={t('entityPickerSearchPlaceholder')}
              value={searchQuery}
              onChange={handleSearchQueryChange}
              onKeyDown={handleKeyDown}
            />
          </div>
        </div>

        {/* Results */}
        <div className={`${RESULTS_MIN_HEIGHT_CLASS_NAME} flex-1 overflow-y-auto px-3 pb-4`}>
          {isLoading && results.length === 0 ? (
            <div className={`flex ${RESULTS_MIN_HEIGHT_CLASS_NAME} items-center justify-center pb-8`}>
              <AgentTeamsLottieLoader label={t('searchSearching')} className="h-16 w-16" />
            </div>
          ) : results.length === 0 ? (
            <div
              className={`flex ${RESULTS_MIN_HEIGHT_CLASS_NAME} flex-col items-center justify-center gap-1 text-center text-sm text-[var(--color-fg-muted)]`}
            >
              {t('entityPickerNoResults')}
            </div>
          ) : (
            <ul className="space-y-0.5">
              {results.map((entity, index) => (
                <li key={`${entity.entityType}:${entity.id}`}>
                  <button
                    ref={(element) => {
                      optionRefs.current[index] = element;
                    }}
                    type="button"
                    className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left transition-colors ${
                      index === selectedIndex ? 'bg-base-200' : 'hover:bg-base-200/60'
                    }`}
                    onClick={() => onSelectAction(entity)}
                    onMouseMove={() => {
                      if (selectedIndex !== index) setSelectedIndex(index);
                    }}
                  >
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-base-content">
                      {entity.title}
                    </span>
                    <span className="badge badge-sm shrink-0">{entity.badge}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </BaseModal>
  );
};
